import { useRef, useCallback } from "react"

export function useSynchronizedScroll() {
  // Create refs for all table scroll containers
  const adSpendScrollRef = useRef<HTMLDivElement>(null)
  const merScrollRef = useRef<HTMLDivElement>(null)
  const croWebMerchScrollRef = useRef<HTMLDivElement>(null)
  const searchKPIsScrollRef = useRef<HTMLDivElement>(null)
  const metaAdsReachImpressionsScrollRef = useRef<HTMLDivElement>(null)
  const metaAdsAudienceSegmentsScrollRef = useRef<HTMLDivElement>(null)
  const trafficScrollRef = useRef<HTMLDivElement>(null)
  const averageOrderValuesScrollRef = useRef<HTMLDivElement>(null)
  const totalBusinessRevenueScrollRef = useRef<HTMLDivElement>(null)
  const brandScrollRef = useRef<HTMLDivElement>(null)
  const gaRevenueScrollRef = useRef<HTMLDivElement>(null)
  const shopifyRevenueScrollRef = useRef<HTMLDivElement>(null)
  const shopifyCustomerScrollRef = useRef<HTMLDivElement>(null)
  const shopifyEcommRevenueScrollRef = useRef<HTMLDivElement>(null)
  const shopifyRetailRevenueScrollRef = useRef<HTMLDivElement>(null)
  const amazonRevenueScrollRef = useRef<HTMLDivElement>(null)
  const wholesaleScrollRef = useRef<HTMLDivElement>(null)
  const shopifyContributionScrollRef = useRef<HTMLDivElement>(null)
  const revenueBreakdownScrollRef = useRef<HTMLDivElement>(null)
  const brandValueScrollRef = useRef<HTMLDivElement>(null)
  const brandValueBreakdownScrollRef = useRef<HTMLDivElement>(null)
  const digitalEngagementScrollRef = useRef<HTMLDivElement>(null)

  // Refs for config-driven custom groups, keyed by the group's stable id. These
  // don't exist on the legacy dashboard (which uses the static refs above), so
  // they're created lazily the first time a key is requested. Stored in a ref so
  // identity is stable across renders.
  const dynamicScrollRefs = useRef<
    Map<string, React.RefObject<HTMLDivElement>>
  >(new Map())

  // Array of all static scroll refs for easy iteration
  const allScrollRefs = [
    adSpendScrollRef,
    merScrollRef,
    croWebMerchScrollRef,
    searchKPIsScrollRef,
    metaAdsReachImpressionsScrollRef,
    metaAdsAudienceSegmentsScrollRef,
    trafficScrollRef,
    averageOrderValuesScrollRef,
    totalBusinessRevenueScrollRef,
    brandScrollRef,
    gaRevenueScrollRef,
    shopifyRevenueScrollRef,
    shopifyCustomerScrollRef,
    shopifyEcommRevenueScrollRef,
    shopifyRetailRevenueScrollRef,
    amazonRevenueScrollRef,
    wholesaleScrollRef,
    shopifyContributionScrollRef,
    revenueBreakdownScrollRef,
    brandValueScrollRef,
    brandValueBreakdownScrollRef,
    digitalEngagementScrollRef,
  ]

  // Every ref that should participate in scroll sync: the static legacy refs
  // plus any lazily-created custom-group refs.
  const getAllScrollRefs = () => [
    ...allScrollRefs,
    ...dynamicScrollRefs.current.values(),
  ]

  // Synchronized scroll handler - only sync with expanded tables
  const handleSyncScroll = useCallback(
    (scrollLeft: number, currentRef: React.RefObject<HTMLDivElement>) => {
      getAllScrollRefs().forEach(ref => {
        if (ref.current && ref !== currentRef) {
          ref.current.scrollLeft = scrollLeft
        }
      })
    },
    [allScrollRefs],
  )

  // Get scroll refs by table key
  const getScrollRefByKey = useCallback((tableKey: string) => {
    const refMap: Record<string, React.RefObject<HTMLDivElement>> = {
      adSpend: adSpendScrollRef,
      mer: merScrollRef,
      croWebMerch: croWebMerchScrollRef,
      searchKPIs: searchKPIsScrollRef,
      metaAdsReachImpressions: metaAdsReachImpressionsScrollRef,
      metaAdsAudienceSegments: metaAdsAudienceSegmentsScrollRef,
      traffic: trafficScrollRef,
      averageOrderValues: averageOrderValuesScrollRef,
      totalBusinessRevenue: totalBusinessRevenueScrollRef,
      brand: brandScrollRef,
      gaRevenue: gaRevenueScrollRef,
      shopifyRevenue: shopifyRevenueScrollRef,
      shopifyCustomer: shopifyCustomerScrollRef,
      shopifyEcommRevenue: shopifyEcommRevenueScrollRef,
      shopifyRetailRevenue: shopifyRetailRevenueScrollRef,
      amazonRevenue: amazonRevenueScrollRef,
      wholesale: wholesaleScrollRef,
      shopifyContribution: shopifyContributionScrollRef,
      revenueBreakdown: revenueBreakdownScrollRef,
      brandValue: brandValueScrollRef,
      brandValueBreakdown: brandValueBreakdownScrollRef,
      digitalEngagement: digitalEngagementScrollRef,
    }
    if (refMap[tableKey]) {
      return refMap[tableKey]
    }
    // Custom (config-driven) group: get-or-create a stable ref for this id.
    const existing = dynamicScrollRefs.current.get(tableKey)
    if (existing) {
      return existing
    }
    const created = { current: null } as React.RefObject<HTMLDivElement>
    dynamicScrollRefs.current.set(tableKey, created)
    return created
  }, [])

  // Handle table expand with scroll sync
  const handleToggleExpandedWithSync = useCallback(
    (
      tableKey: string,
      isExpanded: boolean,
      onToggleExpanded: (tableKey: string, isExpanded: boolean) => void,
    ) => {
      onToggleExpanded(tableKey, isExpanded)

      // If a table is being expanded, sync it with the current scroll position
      if (isExpanded) {
        // Find the first visible table's scroll position
        const visibleRef = getAllScrollRefs().find(ref => ref.current)
        if (visibleRef?.current) {
          const currentScrollLeft = visibleRef.current.scrollLeft
          // Use setTimeout to ensure the newly expanded table's DOM is ready
          setTimeout(() => {
            getAllScrollRefs().forEach(ref => {
              if (ref.current) {
                ref.current.scrollLeft = currentScrollLeft
              }
            })
          }, 0)
        }
      }
    },
    [allScrollRefs],
  )

  return {
    scrollRefs: {
      adSpendScrollRef,
      merScrollRef,
      croWebMerchScrollRef,
      searchKPIsScrollRef,
      metaAdsReachImpressionsScrollRef,
      metaAdsAudienceSegmentsScrollRef,
      trafficScrollRef,
      averageOrderValuesScrollRef,
      totalBusinessRevenueScrollRef,
      brandScrollRef,
      gaRevenueScrollRef,
      shopifyRevenueScrollRef,
      shopifyCustomerScrollRef,
      shopifyEcommRevenueScrollRef,
      shopifyRetailRevenueScrollRef,
      amazonRevenueScrollRef,
      wholesaleScrollRef,
      shopifyContributionScrollRef,
      revenueBreakdownScrollRef,
      brandValueScrollRef,
      brandValueBreakdownScrollRef,
      digitalEngagementScrollRef,
    },
    handleSyncScroll,
    getScrollRefByKey,
    handleToggleExpandedWithSync,
  }
}
